import levenshtein from 'fast-levenshtein';
import { metaphone } from 'metaphone';
import { normalizeOptionNameForMatching, normalizeTextToWords } from '../../picklist/utils';
import { FUZZY_CONFIDENCE_THRESHOLD } from './picklistSelectionDecision.shared';

export type PicklistOptionForFuzzyMatch = Readonly<{
  pickIndex: number;
  name: string;
}>;

export type PicklistOptionFuzzyScore = Readonly<{
  option: PicklistOptionForFuzzyMatch;
  confidence: number;
  matchedWordLength: number;
  matchStartWordIndex: number;
}>;

export type BestFuzzyPicklistOptionMatch = Readonly<{
  pickIndex: number;
  confidence: number;
  margin: number;
  matchedWordLength: number;
  matchStartWordIndex: number;
}>;

// Converts text into a metaphone token sequence so phonetically similar miss-hears compare closer.
const buildPhoneticSignature = (value: string): string =>
  normalizeTextToWords(value)
    .map((word) => metaphone(word))
    .join(' ');

// Normalized Levenshtein similarity in [0,1], where 1 means exact match.
const calculateSimilarity = (left: string, right: string): number => {
  if (left === right) return 1;
  if (left.length === 0 || right.length === 0) return 0;
  const distance = levenshtein.get(left, right);
  return 1 - distance / Math.max(left.length, right.length);
};

/**
 * Scores candidate vs option name using both textual and phonetic similarity.
 * The max/average blend improves robustness for split/combined terms and homophones.
 */
export const calculateFuzzyScore = (candidate: string, option: string): number => {
  const normalizedCandidate = normalizeOptionNameForMatching(candidate.toLowerCase());
  const normalizedOption = normalizeOptionNameForMatching(option.toLowerCase());
  const textSimilarity = calculateSimilarity(normalizedCandidate, normalizedOption);
  const phoneticSimilarity = calculateSimilarity(
    buildPhoneticSignature(normalizedCandidate),
    buildPhoneticSignature(normalizedOption)
  );
  return Math.max(textSimilarity, phoneticSimilarity, (textSimilarity + phoneticSimilarity) / 2);
};

/**
 * Finds the best fuzzy option match for a dictated phrase and reports confidence + margin
 * over the second-best candidate so callers can apply guarded acceptance thresholds.
 */
export const resolveBestFuzzyPicklistOptionMatch = ({
  phraseWords,
  picklistOptions,
  allowStartAnywhere = false,
  minimumConfidence = FUZZY_CONFIDENCE_THRESHOLD,
}: {
  phraseWords: string[];
  picklistOptions: ReadonlyArray<PicklistOptionForFuzzyMatch>;
  allowStartAnywhere?: boolean;
  minimumConfidence?: number;
}): BestFuzzyPicklistOptionMatch | null => {
  if (phraseWords.length === 0 || picklistOptions.length === 0) {
    return null;
  }

  const scored = picklistOptions
    .flatMap((option): Array<PicklistOptionFuzzyScore> => {
      const optionWords = normalizeTextToWords(
        normalizeOptionNameForMatching(option.name.toLowerCase())
      );
      if (optionWords.length === 0) {
        return [];
      }

      // Allow a +1 word window so split/combined forms like "macro calcs" vs "macrocalcs"
      // can still score highly in fuzzy mode.
      const candidateWindowSizes = Array.from(
        new Set([optionWords.length, optionWords.length + 1])
      ).filter((size) => size <= phraseWords.length);
      const startIndexes = allowStartAnywhere
        ? Array.from({ length: phraseWords.length }, (_, idx) => idx)
        : [0];

      const optionScores = startIndexes.flatMap((startIndex) =>
        candidateWindowSizes.flatMap((candidateWordLength): Array<PicklistOptionFuzzyScore> => {
          const candidateSlice = phraseWords.slice(startIndex, startIndex + candidateWordLength);
          if (candidateSlice.length === 0) {
            return [];
          }

          return [
            {
              option,
              confidence: calculateFuzzyScore(candidateSlice.join(' '), optionWords.join(' ')),
              matchedWordLength: candidateSlice.length,
              matchStartWordIndex: startIndex,
            },
          ];
        })
      );

      if (optionScores.length === 0) {
        return [];
      }

      const bestScoreForOption = optionScores.sort((a, b) => b.confidence - a.confidence)[0];
      return bestScoreForOption != null ? [bestScoreForOption] : [];
    })
    .sort((a, b) => b.confidence - a.confidence);

  if (scored.length === 0) {
    return null;
  }

  const [best, second] = scored;
  if (best.confidence < minimumConfidence) {
    return null;
  }

  return {
    pickIndex: best.option.pickIndex,
    confidence: best.confidence,
    margin: best.confidence - (second?.confidence ?? 0),
    matchedWordLength: best.matchedWordLength,
    matchStartWordIndex: best.matchStartWordIndex,
  };
};
